import { useEffect, useState } from "react";
import { useAuthStore } from "../contexts/AuthContext";
import { useChartDispatch, useChartStore } from "../contexts/ChartContext";
import { useDashboardChartsDispatch, useDashboardChartsStore } from "../contexts/single-dashboard/DashboardChartsContext";
import { useUpdateLoaderData } from "../contexts/DashboardSlots";

export default function useGetCharts() {
    const chartDispatch = useChartDispatch();
    const { isLoadingCharts } = useChartStore()
    const { selectedAccount } = useAuthStore();
    
    useEffect(()=>{
        if(isLoadingCharts) return;
        chartDispatch({type: 'setIsLoadingCharts',payload: true});

        //  for super admins with "Innovare" selected as the account (no id), return template drafts
        let url = selectedAccount.id ? `/api/charts?account=${selectedAccount.id}` : `/api/charts/templates?isDraft=true`;
        fetch(url, {credentials: 'include'})
        .then(r=>r.json())
        .then(data=>{
            chartDispatch({type: 'setCharts',payload: data});
            chartDispatch({type: 'setIsLoadingCharts',payload: false});
        })
    },[
        selectedAccount,
        chartDispatch
    ])
}

// payload: {accountId: , dashboardId: , ChartId: }
export function useDeleteChart(){
    const dashboardChartsDispatch = useDashboardChartsDispatch();

  async function deleteChart(payload) {
    let resp = await fetch(`/api/dashboards/${payload.dashboardId}/charts/${payload.ChartId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (resp.ok){
      const data = await resp.text(); // Directly get the text response
        //update the dashboardChart in context
        dashboardChartsDispatch({ 
            type: "deleteDashboardChart", payload: {dashboardChartId : payload["ChartId"] }  })
      return data
    }
  }
  return deleteChart
}

// return a function that accepts a chart from the list and saves it
export function useEditChart(dashboardId) {
    const chartDispatch = useChartDispatch();
    const dashboardChartsDispatch = useDashboardChartsDispatch();
    const updateLoader = useUpdateLoaderData();
    const { dashboardCharts } = useDashboardChartsStore();

    return async (chart) => {
        if (!chart?.id) return false;

        const resp = await sendChartUpdate(chart, dashboardId);
        if (!resp.ok) return false;

        const data = await resp.json();
        if (!data?.Chart) return false;

        //add the chart to the chart list
        chartDispatch({type: 'addChartToList', payload: data.Chart});

        //update the dashboardChart in context
        dashboardChartsDispatch({type: "updateDashboardCharts", payload: {DashboardCharts: [data]}})

        // update the loader dashboardCharts
        const tmpDashCharts = {
            ...dashboardCharts,
            [data.id]: data
        }

        updateLoader({
          type: "updateDashboardCharts",
          payload: tmpDashCharts,
        });
        
        return data.Chart;
    }
}

export async function sendChartUpdate(chart, dashboardId) {
    return await fetch(`/api/dashboards/${dashboardId}/charts/${chart.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(chart)
    })
}

export function useUpdateChart() {
    // this flickers on and off... may need useState in future to persist
    const chartDispatch = useChartDispatch();
 
    async function updateChart(chart) {
        chartDispatch({type: 'setIsSavingChart',payload: true});
        let resp = await sendChartUpdate(chart);
        if (!resp.ok) return null;
        
        const updatedChart = await resp.json();
        if (!updatedChart) return null;

        // the chart returned here contains more information than the chart list needs, in the future we may want to simplify somehow
        chartDispatch({type: 'updateChartInList', payload: updatedChart});
        chartDispatch({type: 'setIsSavingChart',payload: false});

        return updatedChart;
    };

    return updateChart;
}

// useChartPublishing returns an object with two functions: one for publishing a chart and one for unpublishing
export function useChartPublishing() {
    const { selectedChart } = useChartStore();
    const chartDispatch = useChartDispatch();

    async function updatePublishStatus(status) {
        const updatedChart = {...selectedChart, isPublishedToMarketplace: status}

        // update the chart in context
        chartDispatch({type: 'setSelectedChart', payload: updatedChart});

        chartDispatch({type: 'setIsLoadingTemplates',payload: true});
        chartDispatch({type: 'setIsSavingChart',payload: true});
        chartDispatch({type: 'setIsLoadingCharts',payload: true});
        
        let returnChart;

        const resp = await sendChartUpdate(updatedChart);
        if (!resp.ok) {
            returnChart = null;
        } else {
            returnChart = await resp.json();
            if (!returnChart) returnChart = null;
        }
            
        chartDispatch({type: 'setIsSavingChart',payload: false});
        return returnChart;
    }

    async function publishChart() {
        if (window.confirm("Are you sure you want to publish?")){
            const chart = await updatePublishStatus(true);
            if (chart?.id) {
                // remove the chart from the list
                chartDispatch({type: 'removeChart',payload: chart});

                // add the chart to templates
                chartDispatch({type: 'addTemplate',payload: chart});
            }

            chartDispatch({type: 'setIsLoadingCharts',payload: false});
            chartDispatch({type: 'setIsLoadingTemplates',payload: false});

            return chart;
        };
    }
    
    async function unpublishChart() {
        if (window.confirm("Are you sure you want to unpublish?")) {
            const chart = await updatePublishStatus(false);
            if (chart?.id) {
                // remove the chart from templates
                chartDispatch({type: 'removeTemplate',payload: chart});

                // add the chart to the list
                chartDispatch({type: 'addChartToList',payload: chart});
            }

            chartDispatch({type: 'setIsLoadingTemplates',payload: false});
            chartDispatch({type: 'setIsLoadingCharts',payload: false});

            return chart;
        };
    }

    return {
        publishChart: publishChart,
        unpublishChart: unpublishChart,
    }
}

// payload: {imgUrl, accountId, chartId}
export function useUpdateChartScreenshot(payload) {
    const chartDispatch = useChartDispatch();
    const [chartObj, setChartObj] = useState(payload)

    async function updateChartScreenshot() {
        const res = await fetch(`/api/charts/${chartObj.id}/screenshot`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(chartObj),
        })
        if (res.ok) {
            const data = await res.json();
            chartDispatch({type: "updateChartScreenshot", payload: data})
            return data
        }
        
    }
    return {chartObj, setChartObj, updateChartScreenshot}
}